import Calculate from './Calculate';

type Type = 'R' | 'W';
type PolicyType = 'COUPON' | 'PROMOTION' | 'GROUP' | 'GENERAL';

type DiscountPolicyType = {
  discount_rate: number;
  discount_type: Type;
  max_discount_price?: number;
};

type ReservePolicyType = {
  reserve_rate: number;
  reserve_type: Type;
};

//  정책
type ProductPolicyType = {
  policy_type: PolicyType;
} & DiscountPolicyType &
  ReservePolicyType;

// 샹폽 기본 정보
type ProductType = {
  price: number;
  option_price?: number;
  num?: number;
};

type ApplyPolicyInfoType = {
  policy_type: PolicyType;
  discount_price: number;
  max_discount_price: number;
} & DiscountPolicyType;

type CalculateApplyPolicyResultType = {
  final_discount_price: number;
  final_reserve_price: number;
  apply_policy_info: ApplyPolicyInfoType[];
};

type CalculateApplyPolicyType = (
  product: ProductType,
  gradePolicy: ProductPolicyType,
  productPolicy?: ProductPolicyType,
  couponPolicy?: ProductPolicyType
) => CalculateApplyPolicyResultType;

const calculateApplyPolicy: CalculateApplyPolicyType = (
  product,
  gradePolicy,
  productPolicy,
  couponPolicy
) => {
  const { price, option_price = 0, num = 1 } = product;
  const totalProductPrice = price + option_price;

  // 총 할인 금액
  let totalDiscountPrice: number = 0;
  // 최종적으로 적립될 적립정책
  let finalRewardPolicy: ReservePolicyType = {
    reserve_rate: 0,
    reserve_type: 'W',
  };
  // 적용된 정책
  let applyPolicies: ApplyPolicyInfoType[] = [];

  // NOTE - 등급 정책 적용
  if (gradePolicy.discount_rate > 0) {
    // 등급 할인금 계산
    const gradeDiscount: number = Calculate.calculateDiscount(
      totalProductPrice,
      1,
      {
        ...gradePolicy,
        max_discount_price: totalProductPrice,
      }
    );

    // 적용된 등급 할인
    const gradeApplyPolicy: ApplyPolicyInfoType = {
      policy_type: gradePolicy.policy_type,
      discount_price: gradeDiscount, // 개당 할인금
      max_discount_price: totalProductPrice,
      discount_rate: gradePolicy.discount_rate,
      discount_type: gradePolicy.discount_type,
    };

    applyPolicies.push(gradeApplyPolicy);
    totalDiscountPrice += gradeDiscount * num;

    finalRewardPolicy = {
      reserve_rate: gradePolicy.reserve_rate,
      reserve_type: gradePolicy.reserve_type,
    };
  }

  // NOTE - 프로모션 정책 적용
  if (productPolicy) {
    // 등급할인 적용 후 상품 개당 금액
    const applyDiscountPrice = totalProductPrice * num - totalDiscountPrice;
    const promotionDiscount: number = Calculate.calculateDiscount(
      applyDiscountPrice / num,
      1,
      {
        ...productPolicy,
        max_discount_price: Math.min(
          productPolicy.max_discount_price ?? 0,
          applyDiscountPrice
        ),
      }
    );

    // 적용된 프로모션 할인
    const promotionApplyPolicy: ApplyPolicyInfoType = {
      policy_type: productPolicy.policy_type,
      discount_price: promotionDiscount, // 개당 할인금
      max_discount_price: productPolicy.max_discount_price ?? 0,
      discount_rate: productPolicy.discount_rate,
      discount_type: productPolicy.discount_type,
    };

    applyPolicies.push(promotionApplyPolicy);
    totalDiscountPrice += promotionDiscount * num;

    finalRewardPolicy = {
      reserve_rate: productPolicy.reserve_rate,
      reserve_type: productPolicy.reserve_type,
    };
  }

  // NOTE - 쿠폰 정책 적용
  if (couponPolicy) {
    // 등급할인 적용 후 상품 개당 금액
    const applyDiscountPrice = totalProductPrice * num - totalDiscountPrice;
    const couponDiscount: number = Calculate.calculateDiscount(
      applyDiscountPrice / num,
      1,
      {
        ...couponPolicy,
        max_discount_price: Math.min(
          couponPolicy.max_discount_price ?? 0,
          applyDiscountPrice
        ),
      }
    );

    // 적용된 쿠폰 할인
    const couponApplyPolicy: ApplyPolicyInfoType = {
      policy_type: couponPolicy.policy_type,
      discount_price: couponDiscount, // 개당 할인금
      max_discount_price: couponPolicy.max_discount_price ?? 0,
      discount_rate: couponPolicy.discount_rate,
      discount_type: couponPolicy.discount_type,
    };

    applyPolicies.push(couponApplyPolicy);
    totalDiscountPrice += couponDiscount * num;

    finalRewardPolicy = {
      reserve_rate: couponPolicy.reserve_rate,
      reserve_type: couponPolicy.reserve_type,
    };
  }

  // NOTE - 최종 적립정책에 따른 적립금
  const finalReservePrice = Calculate.calculateReserve(
    totalProductPrice * num - totalDiscountPrice,
    1,
    finalRewardPolicy
  );

  return {
    final_discount_price: totalDiscountPrice,
    final_reserve_price: finalReservePrice,
    apply_policy_info: applyPolicies,
  };
};

export default calculateApplyPolicy;
